import type { SearchResultRow } from "@softmax/product-ux/search";
import type { Schemas } from "@softmax/observatory-client/types";
import { buildHref } from "@softmax/web-addresses";
import { matchesSearch, formatSearchDate } from "./pill-search-records";
export function portfolioRows({
  seats,
  query,
}: {
  seats: Schemas["LeaguePortfolioSeatPublic"][];
  query: string;
}): SearchResultRow[] {
  return seats
    .filter((seat) =>
      matchesSearch({
        query: query,
        fields: [
          seat.league_name,
          seat.game_name,
          seat.division_name,
          seat.player_name,
          seat.policy_name,
        ],
      }),
    )
    .map((seat) => ({
      key: seat.membership_id,
      href: buildHref({
        name: "watch",
        search: new URLSearchParams({
          league: seat.league_id,
          division: seat.division_id,
        }),
      }),
      label: seat.league_name,
      secondary: `${seat.division_name} · ${seat.policy_name}:v${seat.policy_version}${seat.is_champion ? " · champion" : ""}`,
    }));
}
export function episodeRows({
  episodes,
  seats,
  query,
}: {
  episodes: Schemas["EpisodePage"]["entries"];
  seats: Schemas["LeaguePortfolioSeatPublic"][];
  query: string;
}): SearchResultRow[] {
  const mine = new Set(seats.map((seat) => seat.player_id));
  return episodes
    .map((episode) => ({
      episode,
      opponents: episode.policies
        .filter((policy) => !policy.player_id || !mine.has(policy.player_id))
        .map(
          (policy) => `${policy.policy_name ?? "?"}:v${policy.version ?? "?"}`,
        ),
    }))
    .filter(({ episode, opponents }) =>
      matchesSearch({
        query: query,
        fields: [
          ...opponents,
          episode.coworld_name,
          ...Object.values(episode.tags ?? {}).map(String),
        ],
      }),
    )
    .map(({ episode, opponents }) => ({
      key: episode.episode_id,
      href: buildHref({ name: "episode", episodeId: episode.episode_id }),
      label: `vs ${opponents.slice(0, 3).join(", ") || "yourself"}`,
      secondary: [episode.coworld_name, formatSearchDate(episode.created_at)]
        .filter(Boolean)
        .join(" · "),
    }));
}

export function practiceSearchRows({
  rows,
  query,
}: {
  readonly rows: readonly Schemas["V2ExperienceRequestRow"][];
  readonly query: string;
}): SearchResultRow[] {
  return rows
    .filter((run) =>
      matchesSearch({
        query: query,
        fields: [run.title, run.description, run.coworld_name, run.status],
      }),
    )
    .map((run) => ({
      key: run.id,
      href: buildHref({ name: "experienceRequest", requestId: run.id }),
      label: run.title ?? `${run.coworld_name} practice`,
      secondary: `${run.status} · ${run.completed_count}/${run.episode_count} episodes · ${formatSearchDate(run.created_at)}`,
    }));
}
